import {
  Alert,
  Box,
  Card,
  CardContent,
  Link,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";

import { MY_PERF_SCROLL_LIST_PB } from "../../Dashboard/layouts";
import {
  getNotificationAlertStyles,
  resolveNotificationStyleKey,
  toMuiAlertSeverity,
} from "./notificationAlertStyles";

function RowSkeleton() {
  return (
    <Box
      sx={{
        borderRadius: "8px",
        border: "1px solid #E6E6E6",
        bgcolor: "#F8FAFC",
        px: 2,
        py: 1.5,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
      >
        <Skeleton variant="text" width="72%" height={20} />
        <Skeleton variant="text" width={56} height={20} />
      </Stack>
    </Box>
  );
}

function DefaultSkeleton({ variant }) {
  return (
    <Stack spacing={1.5}>
      {Array.from({ length: 3 }).map((_, idx) =>
        variant === "row" ? (
          <RowSkeleton key={idx} />
        ) : (
          <Skeleton key={idx} variant="rounded" height={64} />
        ),
      )}
    </Stack>
  );
}

function EmptyState({ layout, message }) {
  if (layout === "centered") {
    return (
      <Box
        sx={{
          minHeight: 120,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography sx={{ color: "#718096", fontSize: 14 }}>
          {message}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", py: 14, textAlign: "center" }}>
      <Typography sx={{ fontSize: 13, color: "#718096" }}>{message}</Typography>
    </Box>
  );
}

/**
 * Shared My Performance card: outlined Card with a title, optional subtitle,
 * and a scrollable list of MUI Alert rows (loading / empty / error).
 *
 * @param {string} title
 * @param {string} [subtitle] Shown under the title (De Novo). Omitting it uses the tighter title spacing of KPI / Tyvaso.
 * @param {Array<object>} [items=[]]
 * @param {boolean} [isLoading=false]
 * @param {boolean} [isError=false]
 * @param {string} [emptyStateText="No data found"]
 * @param {string} [errorStateText] Used when `isError` is true and there are no items.
 * @param {"padded"|"centered"} [emptyStateLayout="padded"] `padded` = De Novo / KPI; `centered` = Tyvaso.
 * @param {"row"|"rounded"} [skeletonVariant="rounded"] `row` = De Novo bordered placeholders; `rounded` = Tyvaso Skeleton bars.
 * @param {function} [renderSkeleton] Full skeleton override. Receives no args.
 * @param {function} [getSeverity] `(item) => "success"|"error"|"warning"|"default"|other`. Default: `item.severity`. Unknown/missing keys fall back to warning (orange). Pass `"default"` for the gray row style (no color-coding).
 * @param {function} [getIcon] `(item) => ReactNode|false`. Default: `false` (no Alert icon).
 * @param {function} [getDescription] `(item) => string`. Default: `item.description`.
 * @param {function} [getItemKey] `(item, index) => string|number`. Default: `item.id`.
 * @param {function} [onExplore] If passed, each row gets an Explore link and space-between layout.
 * @param {boolean} [containOverflow=false] When true, clips CardContent and lets the list scroll (Tyvaso).
 */
export default function NotificationCard({
  title,
  subtitle,
  items = [],
  isLoading = false,
  isError = false,
  emptyStateText = "No data found",
  errorStateText,
  emptyStateLayout = "padded",
  skeletonVariant = "rounded",
  renderSkeleton,
  getSeverity = (item) => item?.severity,
  getIcon = () => false,
  getDescription = (item) => item?.description,
  getItemKey = (item) => item?.id,
  onExplore,
  containOverflow = false,
}) {
  const hasData = Array.isArray(items) && items.length > 0;
  const emptyMessage =
    isError && errorStateText ? errorStateText : emptyStateText;

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: "8px",
        border: "0.5px solid #F0F0F0",
        boxShadow: "1px 4px 4px 0px #00000008",
        height: "100%",
      }}
    >
      <CardContent
        sx={{
          p: "24px",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          ...(containOverflow ? { overflow: "hidden" } : null),
        }}
      >
        {subtitle ? (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, fontSize: 14 }}
            >
              {title}
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                color: "#718096",
                mt: 0.75,
                lineHeight: 1.3,
              }}
            >
              {subtitle}
            </Typography>
          </Box>
        ) : (
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, mb: 2, fontSize: 14, flexShrink: 0 }}
          >
            {title}
          </Typography>
        )}

        <Box
          sx={{
            overflowY: "auto",
            flex: 1,
            pr: 1,
            pb: MY_PERF_SCROLL_LIST_PB,
            ...(containOverflow ? { minHeight: 0 } : null),
          }}
        >
          {isLoading ? (
            renderSkeleton ? (
              renderSkeleton()
            ) : (
              <DefaultSkeleton variant={skeletonVariant} />
            )
          ) : hasData ? (
            <Stack spacing={1.5}>
              {items.map((item, index) => {
                const styleKey = resolveNotificationStyleKey(getSeverity(item));
                const styles = getNotificationAlertStyles(styleKey);
                const icon = getIcon(item);
                const showExplore = typeof onExplore === "function";

                return (
                  <Alert
                    key={getItemKey(item, index) ?? index}
                    severity={toMuiAlertSeverity(styleKey)}
                    variant="outlined"
                    icon={icon}
                    sx={{
                      borderRadius: "8px",
                      alignItems: "center",
                      bgcolor: styles.bgcolor,
                      borderColor: styles.borderColor,
                      ...(icon
                        ? {
                            "& .MuiAlert-icon": {
                              color: styles.iconColor,
                              mt: "2px",
                            },
                          }
                        : null),
                      ...(showExplore
                        ? { "& .MuiAlert-message": { width: "100%" } }
                        : null),
                    }}
                  >
                    {showExplore ? (
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={2}
                        sx={{ width: "100%" }}
                      >
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: "#2D2D2D",
                            lineHeight: 1.4,
                          }}
                        >
                          {getDescription(item)}
                        </Typography>
                        <Link
                          variant="text"
                          size="small"
                          onClick={() => onExplore(item)}
                          underline="hover"
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: 13,
                            minWidth: "auto",
                            px: 1,
                            color: "#E56800",
                            flexShrink: 0,
                            cursor: "pointer",
                          }}
                        >
                          Explore
                        </Link>
                      </Stack>
                    ) : (
                      <Typography
                        sx={{ color: "#2D2D2D", fontSize: 13, lineHeight: 1.4 }}
                      >
                        {getDescription(item)}
                      </Typography>
                    )}
                  </Alert>
                );
              })}
            </Stack>
          ) : (
            <EmptyState layout={emptyStateLayout} message={emptyMessage} />
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
